'use client';

import {
  listFeedbackFormCatalog,
  listFeedbackMailCatalog,
  type FeedbackTemplateLocale,
  type ResolvedFeedbackFormCatalogItem,
  type ResolvedFeedbackMailCatalogItem,
} from '@up2stay/up2promo-commons/feedback';
import {
  FeedbackInviteLocale,
  type FeedbackQuestionType,
  type CreateFeedbackFormTemplateDto,
  type CreateFeedbackMailTemplateDto,
} from '@up2stay/up2promo-api-client';
import {
  Button,
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  cn,
} from '@up2stay/up2promo-ui';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import {
  useCreateFeedbackFormTemplate,
  useCreateFeedbackMailTemplate,
} from '@/core/hooks/use-feedback';
import { useFeedbackMailSample } from '@/core/hooks/use-feedback-mail-sample';
import { useIsMobile } from '@/core/hooks/use-mobile';
import {
  type FeedbackMailSampleVars,
  interpolateFeedbackMailPreview,
} from '@/core/lib/feedback-mail-preview';
import { resolveFeedbackLibraryPickerLocale } from '@/core/lib/resolve-feedback-catalog-locale';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { useCurrentWorkspace } from '@/core/store/workspace.store';

export type FeedbackTemplateLibraryKind = 'form' | 'mail';

type FeedbackTemplateLibraryPickerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  kind: FeedbackTemplateLibraryKind;
  /** Called after a catalog item is copied into the workspace. */
  onAdded?: (templateId: string) => void;
};

export function FeedbackTemplateLibraryPicker({
  open,
  onOpenChange,
  kind,
  onAdded,
}: FeedbackTemplateLibraryPickerProps) {
  const dict = useDictionary().feedback.libraryPicker;
  const isMobile = useIsMobile();
  const mailSample = useFeedbackMailSample();
  const workspace = useCurrentWorkspace();
  const workspaceId = workspace?.id;

  const createForm = useCreateFeedbackFormTemplate(workspaceId);
  const createMail = useCreateFeedbackMailTemplate(workspaceId);

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [localeOverride, setLocaleOverride] =
    useState<FeedbackTemplateLocale | null>(null);
  const [adding, setAdding] = useState(false);

  const effectiveLocale = resolveFeedbackLibraryPickerLocale(localeOverride);

  const [prevOpen, setPrevOpen] = useState(open);
  if (open && open !== prevOpen) {
    setPrevOpen(open);
    setSelectedKey(null);
    setLocaleOverride(null);
    setAdding(false);
  } else if (!open && open !== prevOpen) {
    setPrevOpen(open);
  }

  const formItems = useMemo(
    () => listFeedbackFormCatalog(effectiveLocale),
    [effectiveLocale],
  );
  const mailItems = useMemo(
    () => listFeedbackMailCatalog(effectiveLocale),
    [effectiveLocale],
  );

  const selectedForm = useMemo(() => {
    if (kind !== 'form' || !selectedKey) return null;
    return formItems.find((item) => item.key === selectedKey) ?? null;
  }, [formItems, kind, selectedKey]);

  const selectedMail = useMemo(() => {
    if (kind !== 'mail' || !selectedKey) return null;
    return mailItems.find((item) => item.key === selectedKey) ?? null;
  }, [mailItems, kind, selectedKey]);

  const items = kind === 'form' ? formItems : mailItems;
  const selected = kind === 'form' ? selectedForm : selectedMail;

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setSelectedKey(null);
      setLocaleOverride(null);
      setAdding(false);
    }
    onOpenChange(next);
  };

  const handleAdd = async () => {
    if (!selected || !workspaceId) return;
    setAdding(true);
    try {
      if (kind === 'form' && selectedForm) {
        const body = toCreateFormDto(selectedForm);
        const created = await createForm.mutateAsync(body);
        toast.success(dict.addedForm);
        handleOpenChange(false);
        onAdded?.(created.id);
        return;
      }
      if (kind === 'mail' && selectedMail) {
        const body = toCreateMailDto(selectedMail);
        const created = await createMail.mutateAsync(body);
        toast.success(dict.addedMail);
        handleOpenChange(false);
        onAdded?.(created.id);
      }
    } catch {
      toast.error(dict.addFailed);
    } finally {
      setAdding(false);
    }
  };

  return (
    <ResponsiveDialog open={open} onOpenChange={handleOpenChange}>
      <ResponsiveDialogContent
        className={cn(
          'gap-0 p-0 sm:max-w-3xl',
          !isMobile && 'max-h-[85vh] overflow-hidden',
        )}
        fullscreenOnMobile
        fitContent={false}
      >
        <ResponsiveDialogHeader className="border-b border-border/60 px-4 py-4 text-left sm:px-6">
          <ResponsiveDialogTitle>
            {kind === 'form' ? dict.formTitle : dict.mailTitle}
          </ResponsiveDialogTitle>
          <ResponsiveDialogDescription>
            {kind === 'form' ? dict.formDescription : dict.mailDescription}
          </ResponsiveDialogDescription>
          <div className="mt-3 flex items-center gap-2">
            <span className="text-xs text-muted-foreground">
              {dict.languageLabel}
            </span>
            <div className="inline-flex rounded-md border border-border/60 bg-secondary/40 p-0.5">
              {(['de', 'en'] as const).map((locale) => (
                <button
                  key={locale}
                  type="button"
                  className={cn(
                    'rounded-[10px] px-2.5 py-1 text-xs font-medium transition-colors',
                    effectiveLocale === locale
                      ? 'bg-background text-foreground shadow-xs'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                  onClick={() => setLocaleOverride(locale)}
                >
                  {locale === 'de' ? dict.languageDe : dict.languageEn}
                </button>
              ))}
            </div>
          </div>
        </ResponsiveDialogHeader>

        <div
          className={cn(
            'grid min-h-0 flex-1',
            isMobile
              ? 'grid-rows-[1fr_auto]'
              : 'grid-cols-[minmax(0,1fr)_minmax(0,1fr)]',
          )}
        >
          <ul
            className="min-h-0 space-y-1 overflow-y-auto px-3 py-3 sm:px-4"
            key={effectiveLocale}
          >
            {items.map((item) => {
              const active = item.key === selectedKey;
              return (
                <li key={item.key}>
                  <button
                    type="button"
                    className={cn(
                      'w-full rounded-lg px-3 py-3 text-left transition-colors',
                      active
                        ? 'bg-primary-solid text-primary-solid-foreground'
                        : 'hover:bg-muted/60',
                    )}
                    onClick={() => setSelectedKey(item.key)}
                  >
                    <p className="truncate text-sm font-medium">{item.name}</p>
                    <p
                      className={cn(
                        'mt-0.5 line-clamp-2 text-xs',
                        active
                          ? 'text-primary-solid-foreground'
                          : 'text-muted-foreground',
                      )}
                    >
                      {item.description}
                    </p>
                  </button>
                </li>
              );
            })}
          </ul>

          <div
            className={cn(
              'min-h-0 overflow-y-auto border-border/60 bg-muted/20 px-4 py-4 sm:px-5',
              isMobile ? 'border-t' : 'border-l',
            )}
          >
            {!selected ? (
              <p className="text-sm text-muted-foreground">{dict.selectHint}</p>
            ) : kind === 'form' && selectedForm ? (
              <FormPreview item={selectedForm} labels={dict} />
            ) : kind === 'mail' && selectedMail ? (
              <MailPreview
                item={selectedMail}
                sample={mailSample}
                labels={dict}
              />
            ) : null}
          </div>
        </div>

        <ResponsiveDialogFooter className="border-t border-border/60 px-4 py-3 sm:px-6">
          <Button
            type="button"
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={adding}
          >
            {dict.cancel}
          </Button>
          <Button
            type="button"
            onClick={() => void handleAdd()}
            disabled={!selected || adding}
          >
            {adding ? dict.adding : dict.add}
          </Button>
        </ResponsiveDialogFooter>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}

function FormPreview({
  item,
  labels,
}: {
  item: ResolvedFeedbackFormCatalogItem;
  labels: {
    guestTitle: string;
    questions: string;
    questionCount: string;
  };
}) {
  return (
    <div className="space-y-4">
      <div>
        <p className="text-xs font-medium text-muted-foreground">
          {labels.guestTitle}
        </p>
        <p className="mt-1 text-sm font-medium">{item.title}</p>
      </div>
      <div>
        <p className="text-xs font-medium text-muted-foreground">
          {labels.questions}{' '}
          <span className="font-normal">
            (
            {labels.questionCount.replace(
              '{count}',
              String(item.questions.length),
            )}
            )
          </span>
        </p>
        <ol className="mt-2 space-y-2">
          {item.questions.map((question, index) => (
            <li
              key={`${item.key}-${index}`}
              className="rounded-md border border-border/50 bg-card px-3 py-2 text-sm"
            >
              <span className="text-muted-foreground">{index + 1}. </span>
              {question.label}
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

function MailPreview({
  item,
  sample,
  labels,
}: {
  item: ResolvedFeedbackMailCatalogItem;
  /** Same example values as the mail editor (DEV-769). */
  sample: FeedbackMailSampleVars;
  labels: {
    subject: string;
    body: string;
    reminders: string;
    reminderCount: string;
  };
}) {
  return (
    <div className="space-y-4">
      <div>
        <p className="text-xs font-medium text-muted-foreground">
          {labels.subject}
        </p>
        <p className="mt-1 text-sm font-medium">
          {interpolateFeedbackMailPreview(item.subject, sample)}
        </p>
      </div>
      <div>
        <p className="text-xs font-medium text-muted-foreground">
          {labels.body}
        </p>
        <pre className="mt-1 whitespace-pre-wrap font-sans text-sm leading-relaxed text-foreground">
          {interpolateFeedbackMailPreview(item.body, sample)}
        </pre>
      </div>
      {item.reminders.length > 0 ? (
        <p className="text-xs text-muted-foreground">
          {labels.reminders}:{' '}
          {labels.reminderCount.replace(
            '{count}',
            String(item.reminders.length),
          )}
        </p>
      ) : null}
    </div>
  );
}

function toCreateFormDto(
  item: ResolvedFeedbackFormCatalogItem,
): CreateFeedbackFormTemplateDto {
  return {
    name: item.name,
    description: item.description,
    title: item.title,
    questions: item.questions.map((question) => ({
      type: question.type as FeedbackQuestionType,
      label: question.label,
      required: question.required,
      options: question.options,
    })),
  };
}

function toCreateMailDto(
  item: ResolvedFeedbackMailCatalogItem,
): CreateFeedbackMailTemplateDto {
  return {
    name: item.name,
    description: item.description,
    locale:
      item.locale === 'en' ? FeedbackInviteLocale.EN : FeedbackInviteLocale.DE,
    subject: item.subject,
    body: item.body,
    reminders: item.reminders.map((reminder) => ({
      delayDays: reminder.delayDays,
      subject: reminder.subject,
      body: reminder.body,
    })),
  };
}
